import { Box, Pressable, Row, Text } from 'native-base'
import React, { useCallback, useEffect, useState } from 'react'

import { Icon, PointsIndicator } from '~components'
import { useDeviceInfo, useNavigation, useTranslation } from '~hooks'
import { getNavigationTabIconName } from '~navigation/helpers'
import { useGetUser } from '~query-hooks'
import { Routes } from '~types/routesNames'

export type StackForTab = Routes.HistoryStack | Routes.OffersStack | Routes.UserStack

const tabsStack = [
  {
    name: Routes.OffersStack,
    i18nKey: 'offers_stack',
    screens: [Routes.OffersList] as string[],
  },
  {
    name: Routes.HistoryStack,
    i18nKey: 'history_stack',
    screens: [Routes.History] as string[],
  },
  {
    name: Routes.UserStack,
    i18nKey: 'user_stack',
    screens: [Routes.UserProfile] as string[],
  },
] as const

type Props = { currentRouteName: string }

export const WebNavBar = ({ currentRouteName }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { deviceSpecificWidth, shouldApplyDesktopStyles, shouldApplyWideScreenStyles } =
    useDeviceInfo()

  const { user } = useGetUser()
  const { points = '' } = user || {}
  const [activeTab, setActiveTab] = useState<StackForTab>(Routes.OffersStack)
  const { navigate } = useNavigation()

  useEffect(() => {
    if (currentRouteName) {
      const currentStack = tabsStack.find((stack) => stack.screens.includes(currentRouteName))
      if (currentStack) {
        setActiveTab(currentStack.name)
      }
    }
  }, [currentRouteName])

  const handleTabPress = useCallback(
    (stackName: StackForTab) => {
      setActiveTab(stackName)
      const screenToNavigate = tabsStack.find((stack) => stack.name === stackName)?.screens[0]

      // @ts-expect-error: react navigation works differently on web and it's hard to add types that satisfy web and mobile
      navigate(screenToNavigate || Routes.OffersList)
    },
    [navigate, setActiveTab]
  )

  return (
    <Box w="full" bgColor="white">
      <Row
        alignItems="center"
        bgColor="white"
        justifyContent="space-between"
        py={points ? '2' : '3'}
        w={deviceSpecificWidth}
        mx="auto"
      >
        <Row flex={1} {...(!shouldApplyWideScreenStyles && { justifyContent: 'space-evenly' })}>
          {tabsStack.map(({ name, i18nKey }) => {
            const focused = activeTab === name
            return (
              <Box key={name} mx="4">
                <Box>
                  <Pressable
                    flexDir={shouldApplyWideScreenStyles ? 'row' : 'column'}
                    // eslint-disable-next-line react/jsx-no-bind
                    onPress={() => handleTabPress(name)}
                    {...(!shouldApplyWideScreenStyles && { alignItems: 'center' })}
                  >
                    <Icon
                      name={getNavigationTabIconName({ routeName: name, focused })}
                      size={20}
                      color={focused ? 'actionBlue' : 'offMain'}
                    />
                    <Text bold color={focused ? 'actionBlue' : 'offMain'} ml="1">
                      {t(`navigation.screen_titles.${i18nKey}`)}
                    </Text>
                  </Pressable>
                  {focused && <Box bgColor="actionBlue" w="full" h="2px" />}
                </Box>
              </Box>
            )
          })}
        </Row>
        <Box>
          {shouldApplyDesktopStyles && points ? (
            <PointsIndicator balance={points || '0'} variant="primary" withText={false} />
          ) : null}
        </Box>
      </Row>
    </Box>
  )
}
